<template>
  <div
    class="quick-action-card"
    tabindex="0"
    role="button"
    :aria-label="`${title}: ${description}`"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
    @keydown.space.prevent="$emit('click')"
  >
    <div class="flex flex-col items-center text-center p-4 space-y-3">
      <!-- Icon -->
      <div class="flex-shrink-0">
        <div
          class="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center"
        >
          <component :is="iconComponent" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {{ title }}
        </h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
          {{ description }}
        </p>
      </div>

      <!-- Badge (optional) -->
      <div v-if="badge" class="flex-shrink-0">
        <span
          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
          :class="badgeColorClass"
        >
          {{ badge }}
        </span>
      </div>
    </div>

    <!-- Hover overlay -->
    <div
      class="absolute inset-0 bg-primary-50 dark:bg-primary-900/20 opacity-0 transition-opacity duration-200 rounded-lg pointer-events-none group-hover:opacity-100"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits } from 'vue';

export interface QuickActionCardProps {
  title: string;
  description: string;
  icon:
    | 'search'
    | 'calculator'
    | 'document'
    | 'chat'
    | 'settings'
    | 'analytics'
    | 'upload'
    | 'download';
  badge?: string;
  badgeColor?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  disabled?: boolean;
}

const props = withDefaults(defineProps<QuickActionCardProps>(), {
  badgeColor: 'primary',
  disabled: false,
});

defineEmits<{
  click: [];
}>();

// Icon components mapping
const iconComponent = computed(() => {
  // Return SVG component based on icon prop
  switch (props.icon) {
    case 'search':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        `,
      };
    case 'calculator':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        `,
      };
    case 'document':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        `,
      };
    case 'chat':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        `,
      };
    case 'settings':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        `,
      };
    case 'analytics':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        `,
      };
    case 'upload':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        `,
      };
    case 'download':
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
        `,
      };
    default:
      return {
        template: `
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        `,
      };
  }
});

const badgeColorClass = computed(() => {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
    success: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200',
    warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200',
    error: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };

  return colorClasses[props.badgeColor] || colorClasses.primary;
});
</script>

<style scoped>
.quick-action-card {
  @apply relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700;
  @apply hover:border-primary-300 dark:hover:border-primary-600;
  @apply focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent;
  @apply transition-all duration-200 cursor-pointer;
  @apply transform hover:scale-105 hover:shadow-lg;
}

.quick-action-card:active {
  @apply transform scale-95;
}

.quick-action-card:disabled,
.quick-action-card[aria-disabled='true'] {
  @apply opacity-50 cursor-not-allowed;
  @apply transform-none hover:scale-100 hover:shadow-none;
}

/* Line clamp utility for description */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .quick-action-card {
    @apply transform-none;
    transition: none;
  }

  .quick-action-card:hover {
    @apply transform-none;
  }

  .quick-action-card:active {
    @apply transform-none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .quick-action-card {
    @apply border-2;
  }

  .quick-action-card:focus {
    @apply ring-4;
  }
}

/* Print styles */
@media print {
  .quick-action-card {
    @apply transform-none shadow-none;
    break-inside: avoid;
  }
}
</style>
